/**
 * Helpers for the knowledge base category hierarchy (WEB-830).
 *
 * The API returns a flat list where each category carries `parent_id`
 * (null for top level) and `is_active` (0/1). These helpers turn that into
 * display order with depth, aggregate article counts up the tree, and give
 * the editor what it needs to avoid creating cycles.
 */

const byOrderThenName = (a, b) =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name);

/** True when the category itself is active (ancestry is handled by the tree walk). */
export const isActiveNode = (node) => node.is_active !== 0 && node.is_active !== false;

/** Flat API list -> array of root nodes, each with sorted `children` (recursive). */
export const buildCategoryTree = (categories) => {
  const byId = new Map(categories.map(c => [c.category_id, { ...c, children: [] }]));

  const roots = [];
  byId.forEach(node => {
    const parent = node.parent_id != null ? byId.get(node.parent_id) : null;
    if (parent) parent.children.push(node);
    else roots.push(node); // top level, or parent not in this list
  });

  const sortDeep = (nodes) => {
    nodes.sort(byOrderThenName);
    nodes.forEach(n => sortDeep(n.children));
  };
  sortDeep(roots);
  return roots;
};

/**
 * Tree -> flat display list (depth-first, so children follow their parent).
 * Each entry carries:
 *   depth              0 for top level, 1 for a sub-category, ...
 *   has_children       whether anything is nested under it
 *   total_count        its own published articles plus everything nested
 *   ancestor_hidden    some category above it is inactive
 *   effectively_hidden it or some category above it is inactive, i.e. the
 *                      public page will not show it regardless of its own flag
 * `filter(node)` returning false drops that node AND its whole subtree, which
 * is how "a hidden parent hides its children" works on the public page.
 */
export const flattenCategoryTree = (roots, filter = () => true) => {
  const out = [];

  const walk = (node, depth, ancestorHidden) => {
    if (!filter(node)) return 0;
    const { children, ...rest } = node;
    const effectivelyHidden = ancestorHidden || !isActiveNode(node);
    const entry = {
      ...rest,
      depth,
      has_children: children.length > 0,
      ancestor_hidden: ancestorHidden,
      effectively_hidden: effectivelyHidden,
    };
    out.push(entry);

    let total = Number(node.article_count ?? 0);
    children.forEach(child => { total += walk(child, depth + 1, effectivelyHidden); });
    entry.total_count = total;
    return total;
  };

  roots.forEach(r => walk(r, 0, false));
  return out;
};

/** Flat API list -> flat display list in one step. */
export const toDisplayList = (categories, filter) =>
  flattenCategoryTree(buildCategoryTree(categories), filter);

/** IDs nested under categoryId (excluding itself), computed from the flat API list. */
export const getDescendantIds = (categories, categoryId) => {
  const childrenOf = new Map();
  categories.forEach(c => {
    if (c.parent_id == null) return;
    if (!childrenOf.has(c.parent_id)) childrenOf.set(c.parent_id, []);
    childrenOf.get(c.parent_id).push(c.category_id);
  });

  const result = [];
  const stack = [...(childrenOf.get(categoryId) || [])];
  while (stack.length) {
    const id = stack.pop();
    result.push(id);
    stack.push(...(childrenOf.get(id) || []));
  }
  return result;
};

/**
 * "— — 🏷 Name (hidden)" style label for <select> options. Native <option>
 * elements ignore CSS indentation, so depth is shown with a text prefix, and
 * an effectively hidden category is marked so an admin filing an article or
 * picking a parent knows the public page will not show it.
 */
export const indentedLabel = (entry) =>
  `${'— '.repeat(entry.depth ?? 0)}${entry.icon ? `${entry.icon} ` : ''}${entry.name}` +
  `${entry.effectively_hidden ? ' (hidden)' : ''}`;
